'use client';

import * as React from 'react';

export type ChipInputOption = {
  code: string;
  classification: string;
  specialization?: string;
};

export type ChipInputProps = {
  value: string[]; // Array of selected codes
  onChange: (value: string[]) => void;
  options?: ChipInputOption[]; // Available options for autocomplete
  placeholder?: string;
  freeSolo?: boolean; // Allow typing arbitrary values (for CPT codes)
  className?: string;
  label?: React.ReactNode; // Support both string and JSX elements (with icons)
  helperText?: string;
};

/**
 * ChipInput Component
 *
 * Displays selected values as removable chips with autocomplete dropdown
 * Supports both structured options (taxonomy codes) and free-solo mode (CPT codes)
 */
export const ChipInput = React.forwardRef<HTMLDivElement, ChipInputProps>(
  (
    {
      value,
      onChange,
      options = [],
      placeholder = 'Type to search...',
      freeSolo = false,
      className,
      label,
      helperText,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState('');
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Filter options based on input
    const filteredOptions = React.useMemo(() => {
      if (!inputValue) return options;

      const query = inputValue.toLowerCase();
      return options.filter(
        option =>
          option.code.toLowerCase().includes(query) ||
          option.classification.toLowerCase().includes(query) ||
          (option.specialization && option.specialization.toLowerCase().includes(query))
      );
    }, [inputValue, options]);

    // Handle adding a chip
    const handleAdd = (code: string) => {
      const trimmed = code.trim();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
      }
      setInputValue('');
      setShowDropdown(false);
      setHighlightedIndex(0);
    };

    // Handle removing a chip
    const handleRemove = (codeToRemove: string) => {
      onChange(value.filter(c => c !== codeToRemove));
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setShowDropdown(true);
      setHighlightedIndex(0);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();

        if (showDropdown && filteredOptions.length > 0) {
          // Select highlighted option
          const selected = filteredOptions[highlightedIndex];
          if (selected) {
            handleAdd(selected.code);
          }
        } else if (freeSolo && inputValue) {
          // Add custom value in freeSolo mode
          handleAdd(inputValue);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        // Remove last chip if input is empty
        handleRemove(value[value.length - 1] || '');
      } else if (e.key === ',') {
        // Allow comma-separated entry in freeSolo mode
        e.preventDefault();
        if (freeSolo && inputValue) {
          handleAdd(inputValue);
        }
      }
    };

    // Handle option click
    const handleOptionClick = (code: string) => {
      handleAdd(code);
      inputRef.current?.focus();
    };

    // Handle input focus
    const handleFocus = () => {
      if (inputValue) {
        setShowDropdown(true);
      }
    };

    // Handle click outside to close dropdown
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (ref && 'current' in ref && ref.current && !ref.current.contains(event.target as Node)) {
          setShowDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ref]);

    // Get display label for a code
    const getDisplayLabel = (code: string): string => {
      const option = options.find(o => o.code === code);
      if (!option) return code;

      return option.specialization
        ? `${code} - ${option.classification} (${option.specialization})`
        : `${code} - ${option.classification}`;
    };

    return (
      <div ref={ref} className={`flex flex-col gap-2 ${className || ''}`}>
        {label && <label className="text-sm font-medium">{label}</label>}

        <div className="relative">
          {/* Chip container with input */}
          <div className="flex flex-wrap gap-2 px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 min-h-[44px] items-center transition-all duration-200 focus-within:bg-background focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20">
            {/* Render chips */}
            {value.map(code => (
              <span
                key={code}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/15 text-primary rounded-lg text-sm border border-primary/20"
              >
                <span className="font-mono">{code}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(code)}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${code}`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="text-primary"
                  >
                    <path
                      d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </span>
            ))}

            {/* Input field */}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              placeholder={value.length === 0 ? placeholder : ''}
              className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
            />
          </div>

          {/* Dropdown */}
          {showDropdown && (filteredOptions.length > 0 || (freeSolo && inputValue)) && (
            <div className="absolute z-50 w-full mt-2 glass-card-bg glass-card-border rounded-xl shadow-lg max-h-[300px] overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => handleOptionClick(option.code)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full text-left px-3 py-2.5 text-sm transition-all duration-200 first:rounded-t-xl last:rounded-b-xl ${
                      index === highlightedIndex ? 'glass-surface-hover' : 'hover:glass-surface'
                    }`}
                  >
                    <div className="font-mono text-primary">{option.code}</div>
                    <div className="text-muted-foreground text-xs mt-0.5">
                      {option.classification}
                      {option.specialization && ` - ${option.specialization}`}
                    </div>
                  </button>
                ))
              ) : freeSolo && inputValue ? (
                <button
                  type="button"
                  onClick={() => handleAdd(inputValue)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:glass-surface transition-all duration-200 rounded-xl"
                >
                  <div className="font-mono text-primary">Add: {inputValue}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">
                    Press Enter or click to add custom code
                  </div>
                </button>
              ) : null}
            </div>
          )}
        </div>

        {helperText && <span className="text-xs text-muted-foreground">{helperText}</span>}
      </div>
    );
  }
);

ChipInput.displayName = 'ChipInput';
